import { useTranslation } from 'react-i18next'
import { Dropdown } from 'primereact/dropdown'
import FilterBar from '../../../components/ui/FilterBar'
import { useGrants } from '../../../auth/useCurrentUser'
import { GRANTS } from '../../../auth/grants'
import type { ActivityLookups, PriorityLevel } from '../types'

/** Ülke filtresinde başa sabitlenen ev sahibi ülke adları (TR/EN). Modül-scope: her render'da yeniden üretilmez. */
const HOME_COUNTRY_NAMES = ['türkiye', 'turkey']

export interface ActivityFilterValue {
  statusId: number | null
  priority: PriorityLevel | null
  eventTypeId: number | null
  invitationTypeId: number | null
  countryId: number | null
  q: string
}

export interface ActivityCounts {
  all: number
  byStatus: Record<number, number>
}

interface ActivityFiltersProps {
  lookups?: ActivityLookups
  /** Sekme sayıları (kapsam-geneli; US4 KPI ile aynı kaynak). Verilmezse sekmeler sayısız gösterilir. */
  counts?: ActivityCounts
  value: ActivityFilterValue
  onChange: (patch: Partial<ActivityFilterValue>) => void
  /** Tüm filtre + aramayı tek adımda başlangıç durumuna döndürür. */
  onClear: () => void
}

/**
 * Etkinlik listesi filtre çubuğu — paylaşılan ui/FilterBar kabuğu (durum sekmeleri + arama + temizle +
 * yenile) üzerine öncelik dropdown'ı slot olarak (Talep Listesi kalıbı; Reuse Mandate). Tüm filtreler VE
 * ile birleşir; öncelik skordan türetilir (boş öncelikliler öncelik seçilince hariç).
 */
export default function ActivityFilters({
  lookups,
  counts,
  value,
  onChange,
  onClear,
}: Readonly<ActivityFiltersProps>) {
  const { t } = useTranslation()
  // Katılım Durumu filtresi, kolonla aynı yetkiye bağlı: yetki yoksa filtre de gizlenir (kolonla tutarlı).
  const { hasGrant } = useGrants()
  const showStatusFilter = hasGrant(GRANTS.ACTIVITY_PARTICIPATION_STATUS_VIEW)

  // Ülke filtresinde ev sahibi ülke (Türkiye) başa sabitlenir; kalanı backend sırasında (ad).
  const countriesHomeFirst = (() => {
    const items = lookups?.countries ?? []
    const isHome = (n: string) => HOME_COUNTRY_NAMES.includes(n.toLocaleLowerCase('tr-TR'))
    return [...items.filter((c) => isHome(c.name)), ...items.filter((c) => !isHome(c.name))]
  })()

  const hasActiveFilter =
    value.statusId != null ||
    value.priority != null ||
    value.eventTypeId != null ||
    value.invitationTypeId != null ||
    value.countryId != null ||
    value.q.trim() !== ''

  // Lookup filtre dropdown'u (etkinlik/davet türü, ülke) — hepsi aynı kalıp: başta "Tümü", seçiliyken
  // "<Etiket>: <ad>". null = filtresiz.
  type FilterKey = 'eventTypeId' | 'invitationTypeId' | 'countryId'
  const lookupFilter = (
    key: FilterKey,
    items: { id: number; name: string }[] | undefined,
    labelKey: string,
    allKey: string,
    searchable = false,
    widthClass = 'tk-filter--wide',
  ) => (
    <Dropdown
      className={`tk-filter ${widthClass}`}
      aria-label={t(labelKey)}
      value={value[key]}
      filter={searchable}
      options={[
        { label: t('activity.list.filters.all'), value: null as number | null },
        ...(items ?? []).map((i) => ({ label: i.name, value: i.id })),
      ]}
      placeholder={t(allKey)}
      valueTemplate={(opt: { label: string; value: number | null } | null) =>
        `${t(labelKey)}: ${opt && opt.value != null ? opt.label : t('activity.list.filters.all')}`
      }
      onChange={(e) => onChange({ [key]: e.value })}
    />
  )

  // Katılım durumu artık dropdown (sekme değil — yer kazanır). Sayı rozetleri seçenek etiketine taşınır.
  const statusOptions: { label: string; value: number | null }[] = [
    { label: t('activity.list.filters.all'), value: null },
    ...(lookups?.activityStatuses ?? []).map((s) => ({
      label: counts ? `${s.name} (${counts.byStatus[s.id] ?? 0})` : s.name,
      value: s.id,
    })),
  ]

  const priorityOptions: { label: string; value: PriorityLevel | null }[] = [
    { label: t('activity.list.filters.priorityAll'), value: null },
    { label: t('activity.list.priority.high'), value: 'HIGH' },
    { label: t('activity.list.priority.medium'), value: 'MEDIUM' },
    { label: t('activity.list.priority.low'), value: 'LOW' },
  ]

  return (
    <FilterBar
      searchValue={value.q}
      searchPlaceholder={t('activity.list.filters.search')}
      searchAriaLabel={t('activity.list.filters.search')}
      onSearch={(q) => onChange({ q })}
      showClear={hasActiveFilter}
      clearLabel={t('activity.list.filters.clear')}
      onClear={onClear}
    >
      {showStatusFilter && (
        <Dropdown
          className="tk-filter tk-filter--wide"
          aria-label={t('activity.list.columns.status')}
          value={value.statusId}
          options={statusOptions}
          placeholder={t('activity.list.filters.statusAll')}
          valueTemplate={(opt: { label: string; value: number | null } | null) =>
            `${t('activity.list.filters.status')}: ${opt && opt.value != null ? opt.label : t('activity.list.filters.all')}`
          }
          onChange={(e) => onChange({ statusId: e.value as number | null })}
        />
      )}
      <Dropdown
        className="tk-filter tk-filter--narrow"
        aria-label={t('activity.list.filters.priority')}
        value={value.priority}
        options={priorityOptions}
        placeholder={t('activity.list.filters.priorityAll')}
        valueTemplate={(opt: { label: string; value: PriorityLevel | null } | null) =>
          `${t('activity.list.filters.priority')}: ${opt && opt.value != null ? opt.label : t('activity.list.filters.all')}`
        }
        onChange={(e) => onChange({ priority: e.value as PriorityLevel | null })}
      />
      {lookupFilter(
        'eventTypeId',
        lookups?.eventTypes,
        'activity.list.filters.eventType',
        'activity.list.filters.eventTypeAll',
      )}
      {lookupFilter(
        'invitationTypeId',
        lookups?.invitationTypes,
        'activity.list.filters.invitationType',
        'activity.list.filters.invitationTypeAll',
      )}
      {lookupFilter(
        'countryId',
        countriesHomeFirst,
        'activity.list.filters.country',
        'activity.list.filters.countryAll',
        true,
        'tk-filter--narrow',
      )}
    </FilterBar>
  )
}
